import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { KeyRound, Save, ShieldCheck } from "lucide-react";

const DEFAULT = {
  min_length: 8, require_upper: false, require_lower: false,
  require_number: false, require_symbol: false,
  no_reuse_count: 3, expiry_days: 0,
};

export default function AdminPasswordPolicy() {
  const [p, setP] = useState(DEFAULT);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get("/admin/password-policy");
      setP({ ...DEFAULT, ...(r.data || {}) });
    } catch (e) { toast.error("Could not load policy"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/password-policy", p);
      toast.success("Password policy updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };

  if (loading) return <div className="p-8 text-muted-foreground text-sm">Loading…</div>;

  const Field = ({ label, help, value, onChange, type = "number", min, max }) => (
    <div className="space-y-1.5">
      <Label className="text-foreground/90">{label}</Label>
      <Input type={type} value={value} min={min} max={max}
        onChange={(e) => onChange(type === "number" ? parseInt(e.target.value || "0", 10) : e.target.value)}
        className="bg-background border-border text-foreground focus:border-orange-500" />
      {help && <div className="text-[11px] text-muted-foreground">{help}</div>}
    </div>
  );

  const Toggle = ({ label, help, value, onChange, testid }) => (
    <label className="flex items-start gap-3 p-3 rounded-sm border border-border hover:bg-muted/60 cursor-pointer">
      <Switch checked={!!value} onCheckedChange={onChange} data-testid={testid} />
      <div>
        <div className="text-sm text-foreground">{label}</div>
        {help && <div className="text-[11px] text-muted-foreground mt-0.5">{help}</div>}
      </div>
    </label>
  );

  return (
    <div className="p-8 text-foreground" data-testid="admin-password-policy">
      <div className="mb-6">
        <div className="text-[10px] font-mono uppercase tracking-widest text-orange-600 flex items-center gap-1.5">
          <KeyRound className="w-3 h-3" /> Security
        </div>
        <h1 className="font-heading text-3xl font-bold text-logo-gradient mt-1">Password policy</h1>
        <div className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Global rules applied to every password on the platform — sign-up, forced reset, and
          change-password. Reuse and expiry apply retroactively on next login attempt.
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 max-w-4xl">
        <div className="rounded-sm bg-card border border-border p-5 space-y-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Composition</div>
          <Field label="Minimum length" value={p.min_length} min={6} max={64}
            onChange={(v) => setP({ ...p, min_length: v })}
            help="Recommended: 12 or higher. Never lower than 8." />
          <Toggle label="Require uppercase letter" testid="pp-upper" value={p.require_upper}
            onChange={(v) => setP({ ...p, require_upper: v })} />
          <Toggle label="Require lowercase letter" testid="pp-lower" value={p.require_lower}
            onChange={(v) => setP({ ...p, require_lower: v })} />
          <Toggle label="Require a number" testid="pp-number" value={p.require_number}
            onChange={(v) => setP({ ...p, require_number: v })} />
          <Toggle label="Require a symbol" testid="pp-symbol" value={p.require_symbol}
            onChange={(v) => setP({ ...p, require_symbol: v })} />
        </div>

        <div className="rounded-sm bg-card border border-border p-5 space-y-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Rotation & reuse</div>
          <Field label="No-reuse count" value={p.no_reuse_count} min={0} max={24}
            onChange={(v) => setP({ ...p, no_reuse_count: v })}
            help="Block reusing the last N passwords. 0 disables the check." />
          <Field label="Expiry (days)" value={p.expiry_days} min={0} max={365}
            onChange={(v) => setP({ ...p, expiry_days: v })}
            help="Force reset after N days. 0 disables expiry." />
          <div className="text-[11px] text-muted-foreground flex items-start gap-1.5 pt-2">
            <ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-0.5 text-emerald-600" />
            All password hashes are bcrypt; the previous-hash history is stored for the reuse
            check and never in plain-text.
          </div>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-2">
        <Button onClick={save} disabled={busy} className="rounded-sm bg-gradient-to-r from-orange-600 to-red-600 hover:opacity-90" data-testid="pp-save">
          <Save className="w-4 h-4 mr-1.5" /> {busy ? "Saving…" : "Save policy"}
        </Button>
        <Button onClick={load} variant="outline" className="rounded-sm bg-transparent border-border text-foreground hover:bg-muted" data-testid="pp-reload">
          Discard changes
        </Button>
      </div>
    </div>
  );
}
